import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Divider,
  SelectChangeEvent,
  ListItemIcon,
  ListItemText,
  Checkbox,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestaurantOutlined as FoodIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  LocalMallOutlined as RetailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { 
  getAllProducts, 
  createProduct, 
  updateProduct, 
  deleteProduct, 
  Product,
  getProductsByType
} from '../../firebase/services/productService';
import { 
  getCategories, 
  saveCategories, 
  getProductTypes, 
  saveProductTypes
} from '../../firebase/services/productSettingsService';
import {
  getActiveLocations,
  BusinessLocation,
  initializeDefaultLocations
} from '../../firebase/services/locationService';
import POSLayout from '../../layouts/POSLayout';

// Product form initial state
const initialProductState: Omit<Product, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  price: 0,
  category: '',
  description: '',
  itemType: 'retail',
  sku: '',
  inventory: 0,
  active: true,
  image: '',
  locationIds: []
};

// Keep these for reference and reset functionality only, don't use as defaults
const DEFAULT_FOOD_CATEGORIES = [
  'Appetizers',
  'Sides',
  'Entrees',
  'Desserts',
  'Drinks',
  'Coffee',
  'Alcoholic Beverages'
];

// Keep these for reference and reset functionality only, don't use as defaults
const DEFAULT_RETAIL_CATEGORIES = [
  'Apparel',
  'Beach',
  'Boating',
  'Snacks',
  'Beverages',
  'Souvenirs',
  'Equipment'
];

const ProductsPage: React.FC = () => {
  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState(initialProductState);
  const [filterType, setFilterType] = useState<'all' | 'food' | 'retail'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  
  // State for custom category/type management
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  // Initialize with empty arrays instead of defaults
  const [foodCategories, setFoodCategories] = useState<string[]>([]);
  const [retailCategories, setRetailCategories] = useState<string[]>([]);
  const [newTypeDialogOpen, setNewTypeDialogOpen] = useState(false);
  const [newItemType, setNewItemType] = useState('');
  // Initialize with empty arrays instead of defaults
  const [productTypes, setProductTypes] = useState<string[]>([]);
  
  // State for reset categories dialog
  const [resetCategoriesDialogOpen, setResetCategoriesDialogOpen] = useState(false);
  // State for view settings dialog
  const [viewSettingsDialogOpen, setViewSettingsDialogOpen] = useState(false);
  const [settingsLastLoadedTime, setSettingsLastLoadedTime] = useState<Date | null>(null);
  
  // State for locations
  const [locations, setLocations] = useState<BusinessLocation[]>([]);
  
  // Load settings, locations, and products on component mount
  useEffect(() => {
    loadSettings();
    loadLocations();
    loadProducts();
  }, [filterType]);
  
  // Load product types and categories from Firestore
  const loadSettings = async () => {
    try {
      // Load product types
      const types = await getProductTypes();
      setProductTypes(types);
      
      // Load food categories
      const foodCats = await getCategories('food');
      setFoodCategories(foodCats);
      
      // Load retail categories
      const retailCats = await getCategories('retail');
      setRetailCategories(retailCats);

      // Update last loaded time
      setSettingsLastLoadedTime(new Date());
    } catch (err) {
      console.error('Error loading product settings:', err);
      setError('Failed to load product settings. Please check if productSettings collection exists.');
    }
  };
  
  // Load locations from Firestore
  const loadLocations = async () => {
    try {
      setLoading(true);
      
      // Get active locations
      const activeLocations = await getActiveLocations();
      
      // If no locations exist, initialize default ones
      if (activeLocations.length === 0) {
        console.log('No locations found, initializing defaults...');
        await initializeDefaultLocations();
        const freshLocations = await getActiveLocations();
        setLocations(freshLocations);
        console.log('Locations initialized:', freshLocations);
      } else {
        setLocations(activeLocations);
        console.log('Loaded locations:', activeLocations);
      }
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load locations. Please check the locations collection.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load products from Firestore
  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let productsData: Product[];
      
      if (filterType === 'all') {
        productsData = await getAllProducts();
      } else {
        productsData = await getProductsByType(filterType);
      }
      
      setProducts(productsData);
      
      // Extract categories from products but don't auto-save them
      const uniqueFoodCategories = new Set([...foodCategories]);
      const uniqueRetailCategories = new Set([...retailCategories]);
      
      productsData.forEach(product => {
        if (product.itemType === 'food' && product.category) {
          uniqueFoodCategories.add(product.category);
        } else if (product.itemType === 'retail' && product.category) {
          uniqueRetailCategories.add(product.category);
        }
      });
      
      // Update categories in UI only if we have categories from Firestore
      // Otherwise don't update the UI at all from products
      const newFoodCategories = Array.from(uniqueFoodCategories).sort();
      const newRetailCategories = Array.from(uniqueRetailCategories).sort();
      
      if (foodCategories.length > 0 && JSON.stringify(newFoodCategories) !== JSON.stringify(foodCategories)) {
        setFoodCategories(newFoodCategories);
      }
      
      if (retailCategories.length > 0 && JSON.stringify(newRetailCategories) !== JSON.stringify(retailCategories)) {
        setRetailCategories(newRetailCategories);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open dialog for creating a new product
  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm(initialProductState);
    
    // Reload settings and locations
    Promise.all([
      loadSettings(),
      loadLocations()
    ]).then(([settingsResult, locationsResult]) => {
      if (locations.length === 0) {
        // Force load locations again after initialization
        loadLocations().then(() => {
          console.log('Locations after forced reload:', locations);
        });
      }
      setDialogOpen(true);
    });
  };
  
  // Open dialog for editing a product
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      price: product.price,
      category: product.category,
      description: product.description || '',
      itemType: product.itemType,
      sku: product.sku || '',
      inventory: product.inventory !== undefined ? product.inventory : 0,
      active: product.active,
      image: product.image || '',
      locationIds: product.locationIds || []
    });
    
    // Reload settings and locations
    Promise.all([
      loadSettings(),
      loadLocations()
    ]).then(([settingsResult, locationsResult]) => {
      // If the product's category isn't in our list, add it temporarily for editing
      if (product.itemType === 'food' && !foodCategories.includes(product.category)) {
        setFoodCategories(prev => [...prev, product.category].sort());
      } else if (product.itemType === 'retail' && !retailCategories.includes(product.category)) {
        setRetailCategories(prev => [...prev, product.category].sort());
      }
      
      // Check if product has location IDs that don't exist in loaded locations
      if (product.locationIds && product.locationIds.length > 0) {
        const missingLocations = product.locationIds.filter(
          id => !locations.some(loc => loc.id === id)
        );
        
        if (missingLocations.length > 0) {
          console.warn('Product references locations that no longer exist:', missingLocations);
        }
      }
      
      setDialogOpen(true);
    });
  };
  
  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name) {
      setProductForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle category selection or create new
  const handleCategoryChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    
    if (value === 'add_new_category') {
      setNewCategoryDialogOpen(true);
    } else {
      setProductForm(prev => ({
        ...prev,
        category: value
      }));
    }
  };
  
  // Handle adding a new category
  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) return;
    
    const categoryList = productForm.itemType === 'food' ? foodCategories : retailCategories;
    
    if (!categoryList.includes(newCategory)) {
      let updatedCategories: string[];
      
      if (productForm.itemType === 'food') {
        updatedCategories = [...foodCategories, newCategory].sort();
        setFoodCategories(updatedCategories);
        
        // Save to Firestore
        try {
          await saveCategories('food', updatedCategories);
        } catch (err) {
          console.error('Failed to save food category:', err);
        }
      } else {
        updatedCategories = [...retailCategories, newCategory].sort();
        setRetailCategories(updatedCategories);
        
        // Save to Firestore
        try {
          await saveCategories('retail', updatedCategories);
        } catch (err) {
          console.error('Failed to save retail category:', err);
        }
      }
    }
    
    // Update the form with the new category
    setProductForm(prev => ({
      ...prev,
      category: newCategory
    }));
    
    // Close the dialog and reset the new category field
    setNewCategoryDialogOpen(false);
    setNewCategory('');
  };
  
  // Handle type selection or create new
  const handleItemTypeChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    
    if (value === 'add_new_type') {
      setNewTypeDialogOpen(true);
    } else {
      // Update form and reset category when type changes
      setProductForm(prev => ({
        ...prev,
        itemType: value as 'food' | 'retail',
        category: ''
      }));
    }
  };
  
  // Handle adding a new product type
  const handleAddNewType = async () => {
    if (!newItemType.trim()) return;
    
    if (!productTypes.includes(newItemType)) {
      const updatedTypes = [...productTypes, newItemType].sort();
      setProductTypes(updatedTypes);
      
      // Save to Firestore
      try {
        await saveProductTypes(updatedTypes);
      } catch (err) {
        console.error('Failed to save product type:', err);
      }
    }
    
    // Update the form with the new type
    setProductForm(prev => ({
      ...prev,
      itemType: newItemType as 'food' | 'retail',
      category: '' // Reset category when changing type
    }));
    
    // Close the dialog and reset the new type field
    setNewTypeDialogOpen(false);
    setNewItemType('');
  };
  
  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    
    if (name) {
      setProductForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle toggle for active status
  const handleActiveToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductForm(prev => ({
      ...prev,
      active: e.target.checked
    }));
  };
  
  // Handle location selection
  const handleLocationChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setProductForm(prev => ({
      ...prev,
      locationIds: typeof value === 'string' ? [value] : value
    }));
  };
  
  // Save product (create or update)
  const handleSaveProduct = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!productForm.name || !productForm.category) {
        setError('Name and category are required');
        setLoading(false);
        return;
      }
      
      if (productForm.price <= 0) {
        setError('Price must be greater than zero');
        setLoading(false);
        return;
      }
      
      if (editingProduct) {
        // Update existing product
        await updateProduct(editingProduct.id!, productForm);
      } else {
        // Create new product
        await createProduct(productForm);
      }
      
      // Reset form and close dialog
      setProductForm(initialProductState);
      setDialogOpen(false);
      setEditingProduct(null);
      
      // Reload products
      await loadProducts();
      
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Failed to save product. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle product deletion confirmation
  const handleDeleteConfirm = (productId: string) => {
    setDeleteTargetId(productId);
    setConfirmDeleteOpen(true);
  };
  
  // Delete a product
  const handleDeleteProduct = async () => {
    if (!deleteTargetId) return;
    
    try {
      setLoading(true);
      
      await deleteProduct(deleteTargetId);
      
      // Close dialog and reload products
      setConfirmDeleteOpen(false);
      setDeleteTargetId(null);
      await loadProducts();
      
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter products by search term
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.category.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower) ||
      product.sku?.toLowerCase().includes(searchLower)
    );
  });
  
  // Get relevant categories based on item type
  const getCategoriesForType = (type: 'food' | 'retail') => {
    return type === 'food' ? foodCategories : retailCategories;
  };
  
  // Add save settings button functions
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Save food categories
      await saveCategories('food', foodCategories);
      
      // Save retail categories
      await saveCategories('retail', retailCategories);
      
      // Show success message
      setSuccessMessage('Settings saved successfully!');
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset categories to defaults
  const handleResetCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Reset to defaults
      setFoodCategories([...DEFAULT_FOOD_CATEGORIES]);
      setRetailCategories([...DEFAULT_RETAIL_CATEGORIES]);
      
      // Save to Firestore
      await saveCategories('food', DEFAULT_FOOD_CATEGORIES);
      await saveCategories('retail', DEFAULT_RETAIL_CATEGORIES);
      
      setSuccessMessage('Categories reset to defaults successfully!');
      setResetCategoriesDialogOpen(false);
    } catch (err) {
      console.error('Error resetting categories:', err);
      setError('Failed to reset categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize settings with defaults 
  const handleInitializeSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Initialize locations if needed
      await loadLocations();
      
      // Save default food categories
      await saveCategories('food', DEFAULT_FOOD_CATEGORIES);
      
      // Save default retail categories
      await saveCategories('retail', DEFAULT_RETAIL_CATEGORIES);
      
      // Save default product types
      await saveProductTypes(['food', 'retail']);
      
      // Reload settings
      await loadSettings();
      
      setSuccessMessage('Product settings initialized successfully!');
    } catch (err) {
      console.error('Error initializing settings:', err);
      setError('Failed to initialize settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Show message if no locations exist
  useEffect(() => {
    if (!loading && locations.length === 0) {
      setError('No business locations found. The system will attempt to create default locations.');
      
      // Attempt to initialize locations
      loadLocations();
    }
  }, [loading, locations]);
  
  return (
    <POSLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Products Management</Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
            >
              Add Product
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        
        {/* Show message if no settings exist */}
        {foodCategories.length === 0 && retailCategories.length === 0 && productTypes.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }} action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleInitializeSettings}
              disabled={loading}
            >
              Initialize Settings
            </Button>
          }>
            No product settings found in database. Click "Initialize Settings" to create default categories and types.
          </Alert>
        )}
        
        <Paper sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs
              value={filterType}
              onChange={(_, newValue) => setFilterType(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="all" label="All Products" />
              <Tab value="food" label="Food" icon={<FoodIcon />} iconPosition="start" />
              <Tab value="retail" label="Retail" icon={<RetailIcon />} iconPosition="start" />
            </Tabs>
            
            <TextField
              placeholder="Search products..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ width: 250 }}
            />
          </Box>
        </Paper>
        
        {loading && !dialogOpen ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Inventory</TableCell>
                  <TableCell>Locations</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No products found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={product.itemType === 'food' ? <FoodIcon /> : <RetailIcon />} 
                          label={product.itemType === 'food' ? 'Food' : 'Retail'} 
                          color={product.itemType === 'food' ? 'success' : 'primary'} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">${product.price.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {product.itemType === 'retail' ? 
                          (product.inventory !== undefined ? product.inventory : 'N/A') : 
                          'N/A'}
                      </TableCell>
                      <TableCell>
                        {!product.locationIds || product.locationIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">All Locations</Typography>
                        ) : product.locationIds.length > 2 ? (
                          <Chip 
                            icon={<LocationIcon />} 
                            label={`${product.locationIds.length} locations`} 
                            size="small" 
                            variant="outlined"
                            onClick={() => {
                              setEditingProduct(product);
                              handleEditProduct(product);
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {product.locationIds.map(id => {
                              const location = locations.find(l => l.id === id);
                              return (
                                <Chip 
                                  key={id}
                                  icon={<LocationIcon />} 
                                  label={location ? location.name : id} 
                                  size="small" 
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={product.active ? <ActiveIcon /> : <InactiveIcon />} 
                          label={product.active ? 'Active' : 'Inactive'} 
                          color={product.active ? 'success' : 'error'} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleEditProduct(product)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteConfirm(product.id!)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Add/Edit Product Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Product Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid xs={12}>
                        <TextField
                          label="Product Name"
                          name="name"
                          value={productForm.name}
                          onChange={handleFormChange}
                          fullWidth
                          required
                          margin="normal"
                        />
                      </Grid>
                      <Grid xs={12}>
                        <TextField
                          label="Price"
                          name="price"
                          type="number"
                          value={productForm.price}
                          onChange={handleFormChange}
                          fullWidth
                          required
                          margin="normal"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, step: 0.01 }
                          }}
                        />
                      </Grid>
                      
                      <Grid xs={12}>
                        <FormControl fullWidth margin="normal" required>
                          <InputLabel>Product Type</InputLabel>
                          <Select
                            name="itemType"
                            value={productForm.itemType}
                            onChange={handleItemTypeChange}
                            label="Product Type"
                          >
                            {productTypes.length === 0 ? (
                              <MenuItem disabled>
                                <ListItemText>No product types available. Please initialize settings.</ListItemText>
                              </MenuItem>
                            ) : (
                              productTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type === 'food' ? (
                                    <>
                                      <ListItemIcon>
                                        <FoodIcon fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Food & Beverage</ListItemText>
                                    </>
                                  ) : type === 'retail' ? (
                                    <>
                                      <ListItemIcon>
                                        <RetailIcon fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Retail</ListItemText>
                                    </>
                                  ) : (
                                    type
                                  )}
                                </MenuItem>
                              ))
                            )}
                            <Divider />
                            <MenuItem value="add_new_type">
                              <ListItemIcon>
                                <AddIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Add New Type...</ListItemText>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid xs={12}>
                        <FormControl fullWidth margin="normal" required>
                          <InputLabel>Category</InputLabel>
                          <Select
                            name="category"
                            value={productForm.category}
                            onChange={handleCategoryChange}
                            label="Category"
                            disabled={!productForm.itemType}
                          >
                            {!productForm.itemType ? (
                              <MenuItem disabled>Please select a product type first</MenuItem>
                            ) : getCategoriesForType(productForm.itemType).length === 0 ? (
                              <MenuItem disabled>
                                <ListItemText>No categories available for this type. Please add a category.</ListItemText>
                              </MenuItem>
                            ) : (
                              getCategoriesForType(productForm.itemType).map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))
                            )}
                            <Divider />
                            <MenuItem value="add_new_category">
                              <ListItemIcon>
                                <AddIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Add New Category...</ListItemText>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      {productForm.itemType === 'retail' && (
                        <>
                          <Grid xs={12}>
                            <TextField
                              label="SKU"
                              name="sku"
                              value={productForm.sku}
                              onChange={handleFormChange}
                              fullWidth
                              margin="normal"
                            />
                          </Grid>
                          <Grid xs={12}>
                            <TextField
                              label="Inventory"
                              name="inventory"
                              type="number"
                              value={productForm.inventory}
                              onChange={handleFormChange}
                              fullWidth
                              margin="normal"
                              InputProps={{
                                inputProps: { min: 0 }
                              }}
                            />
                          </Grid>
                        </>
                      )}
                      
                      <Grid xs={12}>
                        <TextField
                          label="Description"
                          name="description"
                          value={productForm.description}
                          onChange={handleFormChange}
                          fullWidth
                          multiline
                          rows={3}
                          margin="normal"
                        />
                      </Grid>
                      
                      <Grid xs={12}>
                        <TextField
                          label="Image URL"
                          name="image"
                          value={productForm.image}
                          onChange={handleFormChange}
                          fullWidth
                          margin="normal"
                          helperText="URL to product image (optional)"
                        />
                      </Grid>
                      
                      <Grid xs={12}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="locations-label">Available Locations</InputLabel>
                          <Select
                            labelId="locations-label"
                            multiple
                            value={productForm.locationIds || []}
                            onChange={handleLocationChange}
                            input={<OutlinedInput label="Available Locations" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">All Locations</Typography>
                                ) : (
                                  selected.map((locationId) => {
                                    const location = locations.find(loc => loc.id === locationId);
                                    return (
                                      <Chip 
                                        key={locationId} 
                                        label={location ? location.name : locationId} 
                                        icon={<LocationIcon />} 
                                      />
                                    );
                                  })
                                )}
                              </Box>
                            )}
                          >
                            {locations.length === 0 ? (
                              <MenuItem disabled>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                  <ListItemText primary="No locations available" secondary="Locations will be loaded or created automatically" />
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      loadLocations();
                                    }}
                                  >
                                    Refresh
                                  </Button>
                                </Box>
                              </MenuItem>
                            ) : (
                              <>
                                <MenuItem disabled>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                    <ListItemText 
                                      primary={`${locations.length} location(s) available`} 
                                      secondary="Select locations or leave empty for all locations" 
                                    />
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        loadLocations();
                                      }}
                                    >
                                      Refresh
                                    </Button>
                                  </Box>
                                </MenuItem>
                                <Divider />
                                {locations.map((location) => (
                                  <MenuItem key={location.id} value={location.id}>
                                    <Checkbox checked={(productForm.locationIds || []).indexOf(location.id as string) > -1} />
                                    <ListItemText 
                                      primary={location.name} 
                                      secondary={`${location.type} - ${location.city}, ${location.state}`} 
                                    />
                                  </MenuItem>
                                ))}
                              </>
                            )}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {locations.length === 0 ? 
                              "Loading locations... If no locations appear, check your locations collection." : 
                              "Select the locations where this product can be sold. If none selected, product will be available at all locations."}
                          </Typography>
                        </FormControl>
                      </Grid>
                      
                      <Grid xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={productForm.active}
                              onChange={handleActiveToggle}
                              name="active"
                              color="primary"
                            />
                          }
                          label="Active"
                          sx={{ mt: 2 }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveProduct} 
              color="primary" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Product'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* New Category Dialog */}
        <Dialog 
          open={newCategoryDialogOpen} 
          onClose={() => setNewCategoryDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Add New Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewCategoryDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleAddNewCategory} color="primary" variant="contained">
              Add Category
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* New Product Type Dialog */}
        <Dialog 
          open={newTypeDialogOpen} 
          onClose={() => setNewTypeDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Add New Product Type</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Type Name"
              fullWidth
              value={newItemType}
              onChange={(e) => setNewItemType(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewTypeDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleAddNewType} color="primary" variant="contained">
              Add Type
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this product? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleDeleteProduct} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Reset Categories Confirmation Dialog */}
        <Dialog open={resetCategoriesDialogOpen} onClose={() => setResetCategoriesDialogOpen(false)}>
          <DialogTitle>Reset Categories</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to reset all categories to default values? 
              This will replace any custom categories you've created.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetCategoriesDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleResetCategories} color="warning" variant="contained">
              Reset to Defaults
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* View Settings Dialog */}
        <Dialog 
          open={viewSettingsDialogOpen} 
          onClose={() => setViewSettingsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Current Product Settings</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Settings last loaded: {settingsLastLoadedTime ? settingsLastLoadedTime.toLocaleString() : 'Never'}
                </Typography>
              </Box>
              <Box>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={loadSettings}
                  sx={{ mr: 1 }}
                >
                  Refresh Settings
                </Button>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={handleInitializeSettings}
                  disabled={
                    productTypes.length > 0 ||
                    foodCategories.length > 0 ||
                    retailCategories.length > 0
                  }
                >
                  Initialize Settings
                </Button>
              </Box>
            </Box>
            
            <Typography variant="h6" gutterBottom>Product Types</Typography>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {productTypes.length === 0 ? (
                <Typography color="text.secondary">No product types defined</Typography>
              ) : (
                productTypes.map(type => (
                  <Chip 
                    key={type} 
                    label={type} 
                    color={type === 'food' ? 'success' : type === 'retail' ? 'primary' : 'default'}
                    icon={type === 'food' ? <FoodIcon /> : type === 'retail' ? <RetailIcon /> : undefined}
                  />
                ))
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Food Categories</Typography>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {foodCategories.length === 0 ? (
                <Typography color="text.secondary">No food categories defined</Typography>
              ) : (
                foodCategories.map(category => (
                  <Chip key={category} label={category} color="success" variant="outlined" />
                ))
              )}
            </Box>
            
            <Typography variant="h6" gutterBottom>Retail Categories</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {retailCategories.length === 0 ? (
                <Typography color="text.secondary">No retail categories defined</Typography>
              ) : (
                retailCategories.map(category => (
                  <Chip key={category} label={category} color="primary" variant="outlined" />
                ))
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewSettingsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </POSLayout>
  );
};

export default ProductsPage; 